/**
 * Saved Models Dialog Component
 *
 * Browser for locally saved IDF models with management options.
 */

import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useFileStore } from '../index';
import type { StoredModel } from '../types';

interface SavedModelsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SavedModelsDialog({ open, onOpenChange }: SavedModelsDialogProps) {
  const { savedModels, loadModel, deleteModel, isLoading } = useFileStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filteredModels = searchQuery.trim()
    ? savedModels.filter((m) =>
        m.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : savedModels;

  const handleOpen = useCallback(
    async (id: string) => {
      try {
        setError(null);
        await loadModel(id);
        onOpenChange(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to open model');
      }
    },
    [loadModel, onOpenChange]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        setError(null);
        await deleteModel(id);
        setDeleteConfirmId(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete model');
        setDeleteConfirmId(null);
      }
    },
    [deleteModel]
  );

  const handleClose = useCallback(() => {
    onOpenChange(false);
    setDeleteConfirmId(null);
  }, [onOpenChange]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex h-[80vh] w-full max-w-3xl flex-col rounded-lg border bg-background shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">Saved Models</h2>
          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="relative">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); }}
                className="w-48 rounded-md border bg-background py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <button
              onClick={handleClose}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-6 mt-4 flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
            <span>{error}</span>
            <button
              onClick={() => { setError(null); }}
              className="ml-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}

        {/* Model list */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : filteredModels.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="mb-4 text-muted-foreground"
              >
                <path d="M20 11.08V8l-6-6H6a2 2 0 0 0-2 2v16c0 1.1.9 2 2 2h6" />
                <path d="M14 3v5h5" />
                <circle cx="18" cy="18" r="3" />
                <path d="m22 22-1.5-1.5" />
              </svg>
              <p className="text-muted-foreground">
                {searchQuery ? 'No models found' : 'No saved models yet'}
              </p>
              <p className="text-sm text-muted-foreground">
                {searchQuery
                  ? 'Try adjusting your search'
                  : 'Save your first model to see it here'}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredModels.map((model) => (
                <SavedModelRow
                  key={model.id}
                  model={model}
                  onOpen={handleOpen}
                  onDelete={handleDelete}
                  showDeleteConfirm={deleteConfirmId === model.id}
                  onShowDeleteConfirm={() => { setDeleteConfirmId(model.id); }}
                  onCancelDelete={() => { setDeleteConfirmId(null); }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface SavedModelRowProps {
  model: StoredModel;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  showDeleteConfirm: boolean;
  onShowDeleteConfirm: () => void;
  onCancelDelete: () => void;
}

function SavedModelRow({
  model,
  onOpen,
  onDelete,
  showDeleteConfirm,
  onShowDeleteConfirm,
  onCancelDelete,
}: SavedModelRowProps) {
  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${String(minutes)} minute${minutes === 1 ? '' : 's'} ago`;
    if (hours < 24) return `${String(hours)} hour${hours === 1 ? '' : 's'} ago`;
    if (days < 7) return `${String(days)} day${days === 1 ? '' : 's'} ago`;

    return date.toLocaleDateString();
  };

  return (
    <div className="flex items-center gap-4 p-4 hover:bg-muted/50">
      {/* Thumbnail */}
      <div className="flex h-12 w-12 items-center justify-center rounded bg-muted">
        {model.thumbnail ? (
          <img
            src={model.thumbnail}
            alt={model.name}
            className="h-full w-full rounded object-cover"
          />
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-muted-foreground"
          >
            <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium truncate">{model.name}</h3>
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span>{formatDate(model.lastModified)}</span>
          <span>{model.metadata.objectCount} objects</span>
          {model.metadata.zoneCount > 0 && (
            <span>{model.metadata.zoneCount} zones</span>
          )}
          {model.metadata.version && (
            <span>v{model.metadata.version}</span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {showDeleteConfirm ? (
          <>
            <span className="text-sm text-muted-foreground mr-2">Delete?</span>
            <Button
              variant="outline"
              size="sm"
              onClick={onCancelDelete}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => { onDelete(model.id); }}
            >
              Delete
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={onShowDeleteConfirm}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
              </svg>
            </Button>
            <Button size="sm" onClick={() => { onOpen(model.id); }}>
              Open
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
